/**
 * Sessions API service
 */
import { apiClient, createFormData } from './client';
import type {
  Session,
  LocalizedSession,
  SessionProgress,
  SessionHistoryItem,
  SessionHistoryLocalizedItem,
  SessionStatistics,
  CategoryStats,
  SessionGroup,
  FavoriteResponse,
  MessageResponse,
  Language,
  SessionType,
} from '@/types';

export const sessionsApi = {
  /**
   * Get all sessions with optional filtering
   * @param params.lang - Optional language code for localized response
   * @param params.session_type - `guided` (the API default: Vođene) or `acoustic` (Akustične, A4)
   */
  getSessions: async (params?: {
    category?: string;
    search?: string;
    lang?: Language;
    session_type?: SessionType;
  }): Promise<Session[] | LocalizedSession[]> => {
    const response = await apiClient.get<Session[] | LocalizedSession[]>('/sessions', { params });
    return response.data;
  },

  /**
   * Get localized sessions (convenience method)
   */
  getLocalizedSessions: async (
    lang: Language,
    params?: { category?: string; search?: string }
  ): Promise<LocalizedSession[]> => {
    const response = await apiClient.get<LocalizedSession[]>('/sessions', {
      params: { ...params, lang },
    });
    return response.data;
  },

  /**
   * Get a single session by ID
   * @param sessionId - Session ID
   * @param lang - Optional language code for localized response
   */
  getSession: async (sessionId: string, lang?: Language): Promise<Session | LocalizedSession> => {
    const params = lang ? { lang } : undefined;
    const response = await apiClient.get<Session | LocalizedSession>(`/sessions/${sessionId}`, { params });
    return response.data;
  },

  /**
   * Get localized session (convenience method)
   */
  getLocalizedSession: async (sessionId: string, lang: Language): Promise<LocalizedSession> => {
    const response = await apiClient.get<LocalizedSession>(`/sessions/${sessionId}`, {
      params: { lang },
    });
    return response.data;
  },

  /**
   * Get session categories with stats
   */
  getCategories: async (): Promise<CategoryStats[]> => {
    const response = await apiClient.get<CategoryStats[]>('/sessions/categories');
    return response.data;
  },

  /**
   * Get the Vođene rows (session groups) in display order, named in `lang`
   */
  getGroups: async (lang: Language): Promise<SessionGroup[]> => {
    const response = await apiClient.get<SessionGroup[]>('/sessions/groups', { params: { lang } });
    return response.data;
  },

  /**
   * Get user's session history
   * @param lang - Optional language code; with it, `title` is in that language and there is no `title_en`
   */
  getHistory: async (lang?: Language): Promise<SessionHistoryItem[] | SessionHistoryLocalizedItem[]> => {
    const params = lang ? { lang } : undefined;
    const response = await apiClient.get<SessionHistoryItem[] | SessionHistoryLocalizedItem[]>('/sessions/history', { params });
    return response.data;
  },

  /**
   * Get user's session statistics
   */
  getStatistics: async (): Promise<SessionStatistics> => {
    const response = await apiClient.get<SessionStatistics>('/sessions/statistics');
    return response.data;
  },

  /**
   * Get recently played sessions
   */
  getRecentSessions: async (): Promise<Session[]> => {
    const response = await apiClient.get<Session[]>('/sessions/recent');
    return response.data;
  },

  /**
   * Start a session (track play start)
   */
  startSession: async (sessionId: string): Promise<MessageResponse & SessionProgress> => {
    const response = await apiClient.post<MessageResponse & SessionProgress>(
      `/sessions/${sessionId}/start`
    );
    return response.data;
  },

  /**
   * Get session progress
   */
  getProgress: async (sessionId: string): Promise<SessionProgress> => {
    const response = await apiClient.get<SessionProgress>(`/sessions/${sessionId}/progress`);
    return response.data;
  },

  /**
   * Update session progress
   */
  updateProgress: async (sessionId: string, progress: number): Promise<MessageResponse & SessionProgress> => {
    const formData = createFormData({ progress: progress.toString() });
    const response = await apiClient.post<MessageResponse & SessionProgress>(
      `/sessions/${sessionId}/progress`,
      formData,
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    return response.data;
  },

  /**
   * Mark session as completed
   */
  completeSession: async (sessionId: string): Promise<MessageResponse & SessionProgress> => {
    const response = await apiClient.post<MessageResponse & SessionProgress>(
      `/sessions/${sessionId}/complete`
    );
    return response.data;
  },

  /**
   * Toggle favorite status for a session
   */
  toggleFavorite: async (sessionId: string): Promise<FavoriteResponse> => {
    const response = await apiClient.post<FavoriteResponse>(
      `/sessions/${sessionId}/favorite`
    );
    return response.data;
  },

  /**
   * Get user's favorite sessions
   * @param lang - Optional language code for localized response
   */
  getFavorites: async (lang?: Language): Promise<Session[] | LocalizedSession[]> => {
    const params = lang ? { lang } : undefined;
    const response = await apiClient.get<Session[] | LocalizedSession[]>('/favorites', { params });
    return response.data;
  },
};

export default sessionsApi;
